import React, { useMemo } from 'react';
import { type CalendarSpace } from '../calendarStorage';
import { type CalendarTaskOccurrence } from '../calendarTaskStorage';
import { useI18n } from '../../../context/I18nContext';

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, index) => index);
export type CalendarReadonlyViewMode = 'month' | 'week' | 'day';

const buildMonthDays = (referenceDate: Date) => {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);
  const today = new Date();

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return {
      date,
      isCurrentMonth: date.getMonth() === month,
      isToday:
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate(),
    };
  });
};

const buildWeekDays = (referenceDate: Date) => {
  const start = new Date(referenceDate);
  const offset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - offset);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
};

const isSameDate = (left: Date, right: Date) => (
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate()
);

type CalendarReadonlyPreviewProps = {
  calendarSpace: CalendarSpace;
  tasks: CalendarTaskOccurrence[];
  viewMode: CalendarReadonlyViewMode;
  onViewModeChange: (mode: CalendarReadonlyViewMode) => void;
  onOpenCalendar: () => void;
};

export const CalendarReadonlyPreview: React.FC<CalendarReadonlyPreviewProps> = ({
  calendarSpace,
  tasks,
  viewMode,
  onViewModeChange,
  onOpenCalendar,
}) => {
  const { t } = useI18n();
  const referenceDate = useMemo(() => new Date(), []);
  const monthDays = useMemo(() => buildMonthDays(referenceDate), [referenceDate]);
  const weekDays = useMemo(() => buildWeekDays(referenceDate), [referenceDate]);
  const monthTitle = useMemo(
    () => referenceDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    [referenceDate]
  );

  const tasksForDate = (date: Date) => tasks
    .filter(task => isSameDate(new Date(task.startAt), date))
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const tasksForSlot = (date: Date, hour: number) => tasksForDate(date)
    .filter(task => new Date(task.startAt).getHours() === hour);

  const renderTask = (task: CalendarTaskOccurrence) => (
    <div
      key={task.occurrenceKey}
      className={`calendar-task-chip ${task.isCompleted ? 'completed' : ''}`}
      style={{ ['--calendar-task-color' as string]: task.headerColor || '#2563eb' }}
    >
      <span>{task.title}</span>
    </div>
  );

  return (
    <section className="boards-calendar-preview" aria-label={t('calendar.previewTitle')}>
      <button type="button" className="boards-calendar-preview-main" onClick={onOpenCalendar}>
        <span className="boards-calendar-preview-kicker">{t('calendar.kicker')}</span>
        <strong>{calendarSpace.title}</strong>
        <span>{monthTitle}</span>
      </button>
      <div className="boards-calendar-preview-toolbar">
        {(['month', 'week', 'day'] as CalendarReadonlyViewMode[]).map(mode => (
          <button key={mode} type="button" className={viewMode === mode ? 'active' : ''} onClick={() => onViewModeChange(mode)}>
            {t(`calendar.view.${mode}`)}
          </button>
        ))}
      </div>

      {viewMode === 'month' && (
        <div className="calendar-grid boards-calendar-readonly-grid">
          {WEEK_DAYS.map(day => <div key={day} className="calendar-weekday">{t(`calendar.weekday.${day.toLowerCase()}`)}</div>)}
          {monthDays.map(day => (
            <div role="button" tabIndex={0} key={day.date.toISOString()} className={['calendar-day', day.isCurrentMonth ? '' : 'muted', day.isToday ? 'today' : ''].filter(Boolean).join(' ')} onClick={onOpenCalendar} onKeyDown={(event) => { if (event.key === 'Enter') onOpenCalendar(); }}>
              <span>{day.date.getDate()}</span>
              <div className="calendar-day-tasks">{tasksForDate(day.date).slice(0, 3).map(renderTask)}</div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'week' && (
        <div className="calendar-week-view boards-calendar-readonly-week">
          <div className="calendar-week-corner" />
          {weekDays.map(day => (
            <button key={day.toISOString()} type="button" className="calendar-week-heading" onClick={onOpenCalendar}>
              <span>{t(`calendar.weekday.${WEEK_DAYS[(day.getDay() + 6) % 7].toLowerCase()}`)}</span>
              <strong>{day.getDate()}</strong>
            </button>
          ))}
          {HOURS.map(hour => (
            <React.Fragment key={hour}>
              <div className="calendar-week-hour">{String(hour).padStart(2, '0')}:00</div>
              {weekDays.map(day => (
                <div key={`${day.toISOString()}-${hour}`} role="button" tabIndex={0} className="calendar-week-slot" onClick={onOpenCalendar} onKeyDown={(event) => { if (event.key === 'Enter') onOpenCalendar(); }}>
                  {tasksForSlot(day, hour).slice(0, 2).map(renderTask)}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}

      {viewMode === 'day' && (
        <div className="calendar-day-view boards-calendar-readonly-day">
          <div className="calendar-day-agenda">
            {HOURS.map(hour => (
              <div key={hour} className="calendar-day-row">
                <span>{String(hour).padStart(2, '0')}:00</span>
                <div role="button" tabIndex={0} className="calendar-day-task-placeholder" onClick={onOpenCalendar} onKeyDown={(event) => { if (event.key === 'Enter') onOpenCalendar(); }}>
                  {tasksForSlot(referenceDate, hour).length ? tasksForSlot(referenceDate, hour).map(renderTask) : t('calendar.emptySlot')}
                </div>
              </div>
            ))}
          </div>
          <aside className="calendar-day-detail">
            <h3>{t('calendar.dayDetails')}</h3>
            <p>{t('calendar.dayDetailsEmpty')}</p>
          </aside>
        </div>
      )}
    </section>
  );
};
